/**
 * Default rows per request. Safe for every `page`/`page_size` endpoint: it is
 * the lowest cap any of them enforces, so a caller that does not know its
 * endpoint's ceiling cannot ask for more than the server accepts.
 */
export const DEFAULT_PAGE_SIZE = 1000;

/**
 * Collect every row of a `page`/`page_size` endpoint, paging until `total` is reached.
 *
 * A single un-paged call silently truncates at whatever page size it asked for.
 * Where the result is rendered as "the whole set" — and especially where it is
 * serialised into a Download that a user may edit and re-upload — a truncated
 * read becomes data loss, because the CoA and mapping uploads replace the entire
 * set rather than merging into it. Raising the page size only moves the cliff;
 * paging removes it.
 *
 * Mirrors `fetchAllPages` in wireDealDetail, which does the same for the
 * `skip`/`limit` mapping endpoints.
 */
export async function fetchAllPaginated<T>(
    fetchPage: (page: number, pageSize: number) => Promise<{ items: T[]; total: number }>,
    pageSize: number = DEFAULT_PAGE_SIZE,
): Promise<T[]> {
    const first = await fetchPage(1, pageSize);
    let items = first.items;
    let page = 1;
    while (items.length < first.total && items.length > 0) {
        page += 1;
        const next = await fetchPage(page, pageSize);
        // A short or empty page means the server has nothing more to give, even
        // if `total` disagrees (rows deleted mid-read) — stop rather than loop.
        if (next.items.length === 0) break;
        items = items.concat(next.items);
    }
    return items;
}
